/**
 * RenderOptions for Form Editor
 * Controls rendering behavior, visibility, and default tab
 *
 * Used by FormEditor to conditionally show/hide UI elements and determine
 * whether to render in modal or embedded mode
 */

/**
 * RenderOptions - Configuration for Form Editor rendering
 * All properties are optional with sensible defaults
 */
export interface RenderOptions {
  /**
   * CSS selector for target element to render editor inline
   * If provided: renders via Portal into that element (embedded mode)
   * If undefined: renders in modal dialog (default)
   * @example "#editor-container"
   * @example ".form-editor-area"
   */
  containerSelector?: string;

  /**
   * Which tab to show first when editor opens
   * @default 'designer'
   */
  defaultTab?: 'designer' | 'json' | 'settings';

  /**
   * Hide the Designer tab
   * @default false
   */
  hideDesignerTab?: boolean;

  /**
   * Hide the JSON tab
   * @default false
   */
  hideJsonTab?: boolean;

  /**
   * Hide the Settings tab
   * @default false
   */
  hideSettingsTab?: boolean;

  /**
   * Hide all navigation tabs (Designer, JSON, Settings)
   * Takes precedence over individual hideXTab flags
   * Shows only the canvas when true
   * @default false
   */
  hideNavigationTabs?: boolean;

  /**
   * Hide the Properties panel (shows control properties when selected)
   * @default false
   */
  hidePropertiesPanel?: boolean;

  /**
   * Hide the Field Toolbox (drag-drop controls palette)
   * @default false
   */
  hideFieldToolbox?: boolean;

  /**
   * Hide the Save button (footer button for persisting changes)
   * @default false
   */
  hideSaveButton?: boolean;
}

/**
 * RenderOptionsResolved - All properties required with defaults applied
 * Used internally by FormEditor after merging with defaults
 * containerSelector can be undefined (modal mode)
 */
export interface RenderOptionsResolved extends Omit<Required<RenderOptions>, 'containerSelector'> {
  containerSelector?: string;
}

/**
 * Default render options
 * Used when no options provided or for merging with user-provided options
 */
export const DEFAULT_RENDER_OPTIONS: RenderOptionsResolved = {
  containerSelector: undefined,
  defaultTab: 'designer',
  hideDesignerTab: false,
  hideJsonTab: false,
  hideSettingsTab: false,
  hideNavigationTabs: false,
  hidePropertiesPanel: false,
  hideFieldToolbox: false,
  hideSaveButton: false,
};

/**
 * Merge user-provided options with defaults
 * Ensures all properties have values
 * @param options - Partial or full RenderOptions
 * @returns RenderOptionsResolved with all properties set
 */
export function resolveRenderOptions(options?: Partial<RenderOptions>): RenderOptionsResolved {
  return {
    ...DEFAULT_RENDER_OPTIONS,
    ...options,
  };
}

/**
 * Type guard to check if value is a valid RenderOptions
 */
export function isRenderOptions(value: unknown): value is RenderOptions {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    (obj.containerSelector === undefined || typeof obj.containerSelector === 'string') &&
    (obj.defaultTab === undefined || ['designer', 'json', 'settings'].includes(obj.defaultTab as string)) &&
    (obj.hideDesignerTab === undefined || typeof obj.hideDesignerTab === 'boolean') &&
    (obj.hideJsonTab === undefined || typeof obj.hideJsonTab === 'boolean') &&
    (obj.hideSettingsTab === undefined || typeof obj.hideSettingsTab === 'boolean') &&
    (obj.hideNavigationTabs === undefined || typeof obj.hideNavigationTabs === 'boolean') &&
    (obj.hidePropertiesPanel === undefined || typeof obj.hidePropertiesPanel === 'boolean') &&
    (obj.hideFieldToolbox === undefined || typeof obj.hideFieldToolbox === 'boolean') &&
    (obj.hideSaveButton === undefined || typeof obj.hideSaveButton === 'boolean')
  );
}
